import { useCallback, useRef, useState } from "react";

import { useSalesRequestRealtimeRefresh } from "~/hooks/useSalesRequestRealtimeRefresh";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

const ACCEPTED_INITIAL_SIZE = 25;
const ACCEPTED_LOAD_MORE_SIZE = 25;

// Shared row mappers, used by both the paged fetch and the realtime refresh.
const extractClientNames = (rows: any[]): Record<string, string> => {
  const names: Record<string, string> = {};
  rows.forEach((req) => {
    if (req.clients && req.clients.name) {
      names[req.clients.id] = req.clients.name;
    }
  });
  return names;
};

const mapRow = (req: any): SalesRequest => ({
  id: req.id,
  clientId: req.client_id,
  createdBy: req.created_by,
  approvedRejectedBy: req.approved_rejected_by || undefined,
  status: req.status as "accepted",
  paid: req.paid,
  paid_amount: req.paid_amount || 0,
  payment_count: 0,
  paymentTerms: req.payment_terms,
  paymentType: req.payment_type as "future" | "prepaid",
  paymentDueDate: req.payment_due_date,
  totalAmount: req.total_amount,
  price_with_iva: req.price_with_iva || undefined,
  isExento: req.is_exento || undefined,
  isNonSale: req.is_non_sale || undefined,
  hasPricingWarning: req.has_pricing_warning || false,
  comments: req.comments || undefined,
  internal_notes: req.internal_notes || undefined,
  factura_observations: req.factura_observations || undefined,
  deliveryDate: req.delivery_date || undefined,
  deliveryAddressId: req.delivery_address_id || undefined,
  deliveryStreet: req.delivery_street || undefined,
  deliveryNumber: req.delivery_number || undefined,
  deliveryComplement: req.delivery_complement || undefined,
  deliveryCity: req.delivery_city || undefined,
  deliveryState: req.delivery_state || undefined,
  deliveryComuna: req.delivery_comuna || undefined,
  rejectedReason: req.rejected_reason || undefined,
  createdAt: req.created_at,
  updatedAt: req.updated_at,
  salesConditionId: req.sales_condition_id || undefined,
  assigned_transportista: req.assigned_transportista || undefined,
  factura_number: (() => {
    const facturaDoc = req.factura_documents?.find(
      (doc: { document_type: string; bsale_number: string | null }) =>
        doc.document_type === "factura" && doc.bsale_number
    );
    return facturaDoc?.bsale_number || undefined;
  })(),
});

export const useAcceptedSalesNotes = (viewAllSellers: boolean = false) => {
  const [acceptedNotes, setAcceptedNotes] = useState<SalesRequest[]>([]);
  const [acceptedClientNames, setAcceptedClientNames] = useState<
    Record<string, string>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  // True once the first fetch settles (success or error), so consumers can
  // distinguish "not started yet" from "finished" (isLoading starts false).
  const [hasLoaded, setHasLoaded] = useState(false);
  const pageRef = useRef(0);
  const isInitialized = useRef(false);

  // Use selectors to avoid rerendering on unrelated store changes
  const currentUserId = useProfileStore((state) => state.id);
  const userRole = useProfileStore((state) => state.role);

  const fetchAcceptedNotes = useCallback(
    async (loadMore: boolean = false) => {
      // Prevent multiple simultaneous calls for initial load
      if (!loadMore && (isLoading || isInitialized.current)) {
        return;
      }

      if (loadMore) {
        if (isLoadingMore) return;
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        pageRef.current = 0;
        setHasMore(true);
        isInitialized.current = true;
      }

      try {
        const currentPage = loadMore ? pageRef.current : 0;
        const pageSize = loadMore
          ? ACCEPTED_LOAD_MORE_SIZE
          : ACCEPTED_INITIAL_SIZE;

        const actualOffset = loadMore
          ? ACCEPTED_INITIAL_SIZE + currentPage * ACCEPTED_LOAD_MORE_SIZE
          : 0;

        let query = supabase
          .from("sales_request")
          .select(
            `
          *,
          paid_amount,
          clients:client_id(id, name, risk_checked),
          profiles:created_by(id, name, lastname, email, role),
          factura_documents:sales_request_documents!sales_request_id(bsale_number, document_type)
        `,
            { count: "exact" }
          )
          .eq("status", "accepted")
          .is("deleted_at", null)
          .range(actualOffset, actualOffset + pageSize - 1);

        // Apply role-based filtering
        if (userRole === "seller" && currentUserId && !viewAllSellers) {
          query = query.eq("created_by", currentUserId);
        } else if (userRole === "transportista") {
          query = query.eq("assigned_transportista", currentUserId);
        }

        const { data, error, count } = await query.order("created_at", {
          ascending: false,
        });

        if (error) throw error;

        if (data) {
          const newClientNames = extractClientNames(data);
          const formattedRequests = data.map(mapRow);

          if (loadMore) {
            setAcceptedNotes((prev) => [...prev, ...formattedRequests]);
            setAcceptedClientNames((prev) => ({ ...prev, ...newClientNames }));
          } else {
            setAcceptedNotes(formattedRequests);
            setAcceptedClientNames(newClientNames);
          }

          // Calculate if there are more records
          const totalFetched = actualOffset + formattedRequests.length;
          setHasMore(
            count ? totalFetched < count : formattedRequests.length === pageSize
          );

          if (loadMore) {
            pageRef.current = currentPage + 1;
          } else {
            pageRef.current = 0;
          }

          return formattedRequests;
        }
      } catch (error) {
        console.error("Error fetching accepted sales notes:", error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        if (!loadMore) setHasLoaded(true);
      }
    },
    [currentUserId, userRole, viewAllSellers]
  );

  const loadMoreAcceptedNotes = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      fetchAcceptedNotes(true);
    }
  }, [fetchAcceptedNotes, isLoadingMore, hasMore]);

  const isRefreshingRef = useRef(false);

  // Silently refresh the currently-loaded window when a sales request changes
  // status, so cards enter/leave this column live (no page reload needed).
  const refreshAcceptedNotes = useCallback(async () => {
    if (!isInitialized.current || isRefreshingRef.current) return;
    isRefreshingRef.current = true;

    try {
      const windowSize =
        ACCEPTED_INITIAL_SIZE + pageRef.current * ACCEPTED_LOAD_MORE_SIZE;

      let query = supabase
        .from("sales_request")
        .select(
          `
          *,
          paid_amount,
          clients:client_id(id, name, risk_checked),
          profiles:created_by(id, name, lastname, email, role),
          factura_documents:sales_request_documents!sales_request_id(bsale_number, document_type)
        `,
          { count: "exact" }
        )
        .eq("status", "accepted")
        .is("deleted_at", null)
        .range(0, windowSize - 1);

      if (userRole === "seller" && currentUserId && !viewAllSellers) {
        query = query.eq("created_by", currentUserId);
      } else if (userRole === "transportista") {
        query = query.eq("assigned_transportista", currentUserId);
      }

      const { data, error, count } = await query.order("created_at", {
        ascending: false,
      });

      if (error) throw error;
      if (!data) return;

      setAcceptedNotes(data.map(mapRow));
      setAcceptedClientNames(extractClientNames(data));
      setHasMore(count ? data.length < count : data.length === windowSize);
    } catch (error) {
      console.error("Error refreshing accepted sales notes:", error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [currentUserId, userRole, viewAllSellers]);

  useSalesRequestRealtimeRefresh(refreshAcceptedNotes);

  return {
    acceptedNotes,
    acceptedClientNames,
    isLoading,
    isLoadingMore,
    hasMore,
    hasLoaded,
    fetchAcceptedNotes,
    loadMoreAcceptedNotes,
  };
};
